/**
 * WorkforceCoverage — where the schedule falls short of demand, and one-click
 * fixes. Data: getCoverage; Apply = applyCoverageSuggestion (awaited; the gap
 * updates only from the op's response — no optimistic success).
 */
import { useMemo, useState } from "react";
import { Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { StatTile, FilterBar, EmptyState, FilterDropdown } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import { useWfResource, useBusy } from "./useWorkforce";
import { getCoverage, applyCoverageSuggestion, type CoverageResponse, type CoverageGap } from "./wf-api";
import {
  WeekNav, LocationFilter, SourceDot, WfPending, severityPill, TinyProgress,
  SOURCE_META, mondayOf, dayLabel, hmRangeLabel,
} from "./wf-shared";
import { useT } from "../../lib/i18n";

export default function WorkforceCoverage() {
  const t = useT();
  const [weekStart, setWeekStart] = useState(() => mondayOf());
  const [locationId, setLocationId] = useState<string | null>(null);
  const [sev, setSev] = useState<"all" | "critical" | "low">("all");
  const [dayFilter, setDayFilter] = useState<string | null>(null);
  const [applied, setApplied] = useState<Record<string, CoverageGap>>({}); // server-confirmed replacements
  const { busy, run } = useBusy();
  const canFix = can("schedules.write");

  const key = `${weekStart}|${locationId ?? "all"}`;
  const { data, loading, error, reload } = useWfResource<CoverageResponse>(`coverage:${key}`, () => getCoverage(weekStart, locationId ?? undefined));

  const gaps = useMemo(() => (data?.gaps ?? []).map((g) => applied[g.id] ?? g), [data, applied]);
  const rows = gaps.filter((g) => (sev === "all" || g.severity === sev) && (!dayFilter || g.date === dayFilter));
  const daysPresent = [...new Set(gaps.map((g) => g.date))].sort();
  const critical = gaps.filter((g) => g.severity === "critical" && g.scheduled < g.needed).length;
  const fixable = gaps.filter((g) => g.suggestions.length > 0 && g.scheduled < g.needed).length;
  const gapHeadHours = gaps.reduce((s, g) => s + Math.max(0, g.needed - g.scheduled) * 0.5, 0);

  const apply = async (gap: CoverageGap, suggestionId: string) => {
    await run(`${gap.id}:${suggestionId}`, async () => {
      try {
        const res = await applyCoverageSuggestion(gap.id, suggestionId);
        setApplied((m) => ({ ...m, [gap.id]: res.gap }));
        toast({ tone: "success", title: t("workforce.coverage.coverageUpdated", "Coverage updated"), subtitle: res.shift ? `Shift ${res.shift.staffName ? `assigned to ${res.shift.staffName}` : "opened for claims"}.` : undefined });
      } catch (e) {
        toast({ tone: "danger", title: t("workforce.coverage.couldnTApplyTheFix", "Couldn't apply the fix"), subtitle: e instanceof Error ? e.message : String(e) });
      }
    });
  };

  const chip = (id: "all" | "critical" | "low", label: string) => (
    <button key={id} data-testid="wfcoverage-severity-chip" data-id={id} onClick={() => setSev(id)}
      style={{ padding: "6px 12px", borderRadius: 999, border: `1px solid ${sev === id ? T.p1 : T.border}`, background: sev === id ? T.p1 : T.white, color: sev === id ? T.white : T.n2, fontSize: 12.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>{label}</button>
  );

  return (
    <Screen rail="wf-coverage">
      <PageHeader title={t("workforce.coverage.coverage", "Coverage")} subtitle={t("workforce.coverage.everyWindowWhereScheduledStaffingFallsShortOfForecastDemandWithThe", "Every window where scheduled staffing falls short of forecast demand — with the fastest fix for each.")} breadcrumb={null} />
      <DataSourceStrip screen="wfcoverage" ops={["getCoverage", "applyCoverageSuggestion"]} />
      <div style={{ padding: "16px 24px 0", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        <StatTile label={t("workforce.coverage.openGaps", "Open gaps")} value={gaps.filter((g) => g.scheduled < g.needed).length} icon={<Icon.warn size={14} />} accent={T.orange1} />
        <StatTile label={t("workforce.coverage.critical", "Critical")} value={critical} icon={<Icon.warn size={14} />} accent={critical > 0 ? T.red1 : T.green1} />
        <StatTile label={t("workforce.coverage.gapStaffHours", "Gap staff-hours")} value={Math.round(gapHeadHours)} icon={<Icon.clock size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("workforce.coverage.oneClickFixable", "One-click fixable")} value={fixable} icon={<Icon.check size={14} />} accent={T.green1} />
      </div>
      <FilterBar>
        <LocationFilter value={locationId} onChange={setLocationId} />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        <WeekNav weekStart={weekStart} onChange={setWeekStart} testid="wfcoverage-weeknav" />
        <span style={{ width: 1, height: 20, background: T.border, margin: "0 4px" }} />
        {chip("all", `All · ${gaps.length}`)}
        {chip("critical", `Critical · ${gaps.filter((g) => g.severity === "critical").length}`)}
        {chip("low", `Low · ${gaps.filter((g) => g.severity === "low").length}`)}
        <FilterDropdown label={dayFilter ? dayLabel(dayFilter, "full") : t("workforce.workforceCoverage.anyDay", "Any day")} active={!!dayFilter} options={daysPresent.map((d) => dayLabel(d, "full"))}
          onPick={(lbl) => setDayFilter(daysPresent.find((d) => dayLabel(d, "full") === lbl) ?? null)} onClear={() => setDayFilter(null)} />
        <span style={{ marginLeft: "auto", fontSize: 12, color: T.soft }}>{rows.length} {t("workforce.coverage.of", "of")}{" "}{gaps.length}</span>
      </FilterBar>
      <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px" }}>
        {error ? <WfPending screen="wfcoverage" opName="getCoverage" error={error} onRetry={reload} />
          : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("workforce.coverage.checkingCoverage", "Checking coverage…")}</div>
          : rows.length === 0 ? (
            <EmptyState icon={<Icon.check size={20} />} title={t("workforce.coverage.noCoverageGaps", "No coverage gaps")} body={t("workforce.coverage.scheduledStaffingMeetsForecastDemandForEveryWindowInThisWeek", "Scheduled staffing meets forecast demand for every window in this week.")} />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {rows.map((g) => {
                const resolved = g.scheduled >= g.needed;
                return (
                  <div key={g.id} data-testid="wfcoverage-gap-row" data-id={g.id}
                    style={{ background: T.white, border: `1px solid ${resolved ? T.border : g.severity === "critical" ? alpha(T.red1, 35) : T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10, opacity: resolved ? 0.72 : 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      {resolved ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 700, color: T.green1 }}><Icon.check size={13} /> {t("workforce.coverage.resolved", "Resolved")}</span> : severityPill(g.severity)}
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{dayLabel(g.date, "full")} · {hmRangeLabel(g.start, g.end)}</span>
                      {g.skill ? <span style={{ fontSize: 11.5, fontWeight: 600, color: T.n2, background: T.n6, borderRadius: 999, padding: "2px 9px" }}>{g.skill}</span> : null}
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: T.soft }}>
                        <SourceDot kind={g.source} /> {g.driverLabel || SOURCE_META[g.source].label}
                      </span>
                      <span style={{ marginLeft: "auto" }}><TinyProgress num={g.scheduled} den={g.needed} /></span>
                    </div>
                    {!resolved && g.suggestions.length > 0 ? (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.05 }}>{t("workforce.coverage.fixes", "Fixes")}</span>
                        {g.suggestions.map((s) => {
                          const busyThis = busy === `${g.id}:${s.id}`;
                          return (
                            <button key={s.id} data-testid="wfcoverage-suggestion-apply" data-id={s.id} disabled={!canFix || busy !== null} onClick={() => apply(g, s.id)}
                              title={canFix ? s.detail : t("workforce.workforceCoverage.needsSchedulesWrite", "Needs schedules.write")}
                              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 999, border: `1px solid ${T.p2}`, background: T.p4, color: T.p1, fontSize: 12.5, fontWeight: 600, cursor: canFix ? "pointer" : "not-allowed", fontFamily: T.font, opacity: busy !== null && !busyThis ? 0.5 : 1 }}>
                              {busyThis ? t("workforce.workforceCoverage.applying", "Applying…") : <>
                                {s.kind === "assign" ? <Icon.user size={12} /> : s.kind === "extend" ? <Icon.clock size={12} /> : s.kind === "swap-in" ? <Icon.refresh size={12} /> : <Icon.plus size={12} />}
                                {s.detail}
                              </>}
                            </button>
                          );
                        })}
                      </div>
                    ) : !resolved ? (
                      <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.coverage.noAutomaticFixAddOrExtendAShiftOnThe", "No automatic fix — add or extend a shift on the")}{" "}<b>{t("workforce.coverage.schedule", "Schedule")}</b> {t("workforce.coverage.screen", "screen.")}</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </Screen>
  );
}
